import { useState } from "react"
import { useNavigate, useSearchParams, Link } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { AuthLayout } from "@/components/auth/AuthLayout"
import { PasswordInput } from "@/components/auth/PasswordInput"
import { FormAlert } from "@/components/auth/FormAlert"
import { authService } from "@/services/authService"

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get("token") ?? ""

  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (password.length < 8) {
      setError("A senha deve ter pelo menos 8 caracteres.")
      return
    }
    if (password !== confirm) {
      setError("As senhas não coincidem.")
      return
    }

    setLoading(true)
    try {
      await authService.resetPassword(token, password)
      setDone(true)
      setTimeout(() => navigate("/login"), 3000)
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } }
      setError(axiosErr.response?.data?.error || "Link inválido ou expirado.")
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <AuthLayout pageTitle="Redefinir senha" title="Link inválido"
        description="Este link de redefinição está incompleto ou já foi usado.">
        <Button asChild size="lg" className="w-full">
          <Link to="/forgot-password">Solicitar nova redefinição</Link>
        </Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      pageTitle="Redefinir senha"
      title="Crie uma nova senha"
      description="Depois de salvar, você entra de novo com a senha nova em todos os dispositivos."
      footer={<Link to="/login" className="font-medium text-foreground underline underline-offset-2">Voltar ao login</Link>}
    >
      {done ? (
        <FormAlert tone="success">Senha redefinida. Levando você para o login...</FormAlert>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="password">Nova senha</Label>
            <PasswordInput id="password" autoComplete="new-password" placeholder="Mínimo de 8 caracteres"
              value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm">Confirmar nova senha</Label>
            <PasswordInput id="confirm" autoComplete="new-password" placeholder="Repita a senha"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </div>

          {error && <FormAlert>{error}</FormAlert>}

          <Button type="submit" size="lg" className="w-full" disabled={loading}>
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Salvando...</> : "Salvar nova senha"}
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
